import React from 'react';
import Layout from '../components/Layout';
import { FiCheckCircle, FiClock, FiCircle, FiFlag } from 'react-icons/fi';
import './Pages.css';

/**
 * Phase-wise Progress/Roadmap Tracker
 *
 * Read from static config. Admin sees full view, users see read-only.
 * This config should match the actual phased rollout plan.
 */
const ROADMAP = [
  {
    phase: 'Phase 1 — Foundation',
    status: 'completed',
    items: [
      { label: 'User authentication (signup, login, JWT)', status: 'completed' },
      { label: 'Ledger management (create, edit, archive, delete)', status: 'completed' },
      { label: 'Transaction CRUD with types (income, expense, payable, receivable)', status: 'completed' },
      { label: 'Category system with backend bookmarks API', status: 'completed' },
      { label: 'Dashboard with summary cards', status: 'completed' },
      { label: 'Reminders system', status: 'completed' },
      { label: 'Reports (monthly, yearly, category)', status: 'completed' },
      { label: 'Receipt uploads', status: 'completed' },
      { label: 'Transfer between ledgers', status: 'completed' },
    ],
  },
  {
    phase: 'Phase 2 — Enhancements',
    status: 'completed',
    items: [
      { label: 'Recurring expenses', status: 'completed' },
      { label: 'Budget tracking with alerts', status: 'completed' },
      { label: 'Vendor tracking system', status: 'completed' },
      { label: 'Offline sync engine (PWA)', status: 'completed' },
      { label: 'Hisab OK Hai Tone (Hindi labels toggle)', status: 'completed' },
      { label: 'Currency-aware icons (INR default)', status: 'completed' },
    ],
  },
  {
    phase: 'Phase 3 — Enterprise & Admin',
    status: 'completed',
    items: [
      { label: 'Auto-assign default ledger for transactions', status: 'completed' },
      { label: 'Dena Hai (Udhari) flow with commitment_date', status: 'completed' },
      { label: 'Special Event Budget type', status: 'completed' },
      { label: 'Vendor payment auto-type (expense_paid)', status: 'completed' },
      { label: 'Bookmark types backend route fix', status: 'completed' },
      { label: 'Multiselect checkbox layout fix', status: 'completed' },
      { label: 'App Level Admin dashboard (role-based)', status: 'completed' },
      { label: 'Subscription & permission system (plans, features)', status: 'completed' },
      { label: 'Phase-wise progress page (this page)', status: 'completed' },
      { label: 'Admin user management (plan change, suspend)', status: 'completed' },
      { label: 'Category bookmark UI (frontend toggle, reorder)', status: 'completed' },
    ],
  },
  {
    phase: 'Phase 3.1 — Help Center (Architecture)',
    status: 'completed',
    description: 'User-facing Help Center for self-serve education. Not developer docs.',
    items: [
      { label: '/help base path implemented', status: 'completed' },
      { label: 'SEO-friendly routing: /help/:category', status: 'completed' },
      { label: 'SEO-friendly routing: /help/:category/:slug', status: 'completed' },
      { label: 'User-facing article system (not developer documentation)', status: 'completed' },
      { label: 'Slug-based URLs (no IDs in URLs)', status: 'completed' },
      { label: 'Category-based navigation', status: 'completed' },
      { label: 'Production-ready structure', status: 'completed' },
    ],
  },
  {
    phase: 'Phase 3.2 — Help Center (Content & Enhancements)',
    status: 'upcoming',
    description: 'Content expansion and UX improvements for Help Center.',
    items: [
      { label: 'Write initial user articles (15–20 articles)', status: 'upcoming' },
      { label: 'Help Home UI enhancements', status: 'upcoming' },
      { label: 'In-app contextual help links', status: 'upcoming' },
      { label: 'Search inside Help Center', status: 'upcoming' },
      { label: 'Related articles linking', status: 'upcoming' },
      { label: 'Feedback system ("Was this helpful?")', status: 'upcoming' },
    ],
  },
  {
    phase: 'Phase 4 — Growth (Upcoming)',
    status: 'upcoming',
    items: [
      { label: 'Category management page with bookmark toggle UI', status: 'completed' },
      { label: 'Multi-currency support (Pro feature)', status: 'upcoming' },
      { label: 'Detailed audit logs', status: 'upcoming' },
      { label: '2FA authentication', status: 'upcoming' },
      { label: 'Admin password reset for users', status: 'upcoming' },
      { label: 'Export to PDF/Excel', status: 'upcoming' },
      { label: 'Push notifications for reminders', status: 'upcoming' },
      { label: 'Advanced analytics dashboard', status: 'upcoming' },
      { label: 'Dark mode support', status: 'upcoming' },
      { label: 'Team/shared ledgers', status: 'upcoming' },
    ],
  },
];

const statusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <FiCheckCircle color="#27AE60" />;
    case 'in_progress':
      return <FiClock color="#F39C12" />;
    case 'upcoming':
      return <FiCircle color="#BDC3C7" />;
    default:
      return <FiCircle color="#BDC3C7" />;
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
      return 'In Progress';
    case 'upcoming':
      return 'Upcoming';
    default:
      return status;
  }
};

const ProgressPage = () => {
  const totalItems = ROADMAP.reduce((sum, phase) => sum + phase.items.length, 0);
  const completedItems = ROADMAP.reduce(
    (sum, phase) => sum + phase.items.filter((i) => i.status === 'completed').length,
    0
  );
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiFlag style={{ marginRight: 8, verticalAlign: 'middle' }} />Development Roadmap</h1>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div className="progress-summary">
            <h3>Overall Progress</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
            </div>
            <p className="progress-text">
              {completedItems} of {totalItems} features completed ({progressPercent}%)
            </p>
          </div>
        </div>

        {ROADMAP.map((phase, phaseIdx) => {
          const phaseCompleted = phase.items.filter((i) => i.status === 'completed').length;
          const phaseTotal = phase.items.length;

          return (
            <div key={phaseIdx} className="card" style={{ marginBottom: 16 }}>
              <div className="progress-phase-header">
                <h2>
                  {statusIcon(phase.status)}
                  <span style={{ marginLeft: 8 }}>{phase.phase}</span>
                </h2>
                <span className={`progress-phase-badge ${phase.status}`}>
                  {statusLabel(phase.status)} ({phaseCompleted}/{phaseTotal})
                </span>
              </div>

              <div className="progress-items">
                {phase.items.map((item, idx) => (
                  <div key={idx} className={`progress-item ${item.status}`}>
                    {statusIcon(item.status)}
                    <span className="progress-item-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default ProgressPage;
